import { useState, useRef, useEffect } from "react";

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      await fetch("https://readdy.ai/api/form/d7deobnsch5bvrijfgl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 md:py-32 bg-[#fdf9f3]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Left: Info */}
          <div>
            <p className="text-[#C9A84C] text-sm tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Hablemos
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-[#1a1208] leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Reserva tu<br />
              <span className="italic text-[#C9A84C]">lectura</span>
            </h2>
            <p className="text-[#5a4a35] text-base leading-relaxed mb-10">
              Escríbeme y te responderé en menos de 24 horas para confirmar tu cita. Estoy aquí para acompañarte en tu camino.
            </p>

            <div className="flex flex-col gap-6">
              <a href="tel:+34643657183" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-[#C9A84C]/50 text-[#C9A84C] group-hover:bg-[#C9A84C]/10 transition-colors duration-200">
                  <i className="ri-phone-line text-base"></i>
                </div>
                <div>
                  <p className="text-[#9a8a75] text-xs uppercase tracking-widest mb-0.5">Teléfono</p>
                  <p className="text-[#3a2e1e] text-sm">+34 643 65 7183</p>
                </div>
              </a>

              <a href="https://wa.me/34643657183" target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-[#C9A84C]/50 text-[#C9A84C] group-hover:bg-[#C9A84C]/10 transition-colors duration-200">
                  <i className="ri-whatsapp-line text-base"></i>
                </div>
                <div>
                  <p className="text-[#9a8a75] text-xs uppercase tracking-widest mb-0.5">WhatsApp</p>
                  <p className="text-[#3a2e1e] text-sm">Escríbeme directamente</p>
                </div>
              </a>
            </div>

            {/* Social */}
            <div className="flex gap-4 mt-10">
              <a href="#" rel="nofollow" className="w-10 h-10 flex items-center justify-center rounded-full border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors duration-200 cursor-pointer">
                <i className="ri-instagram-line text-base"></i>
              </a>
              <a href="#" rel="nofollow" className="w-10 h-10 flex items-center justify-center rounded-full border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors duration-200 cursor-pointer">
                <i className="ri-facebook-line text-base"></i>
              </a>
              <a href="#" rel="nofollow" className="w-10 h-10 flex items-center justify-center rounded-full border border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-colors duration-200 cursor-pointer">
                <i className="ri-tiktok-line text-base"></i>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-8 border border-[#e8dcc8]">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A84C]/15 text-[#C9A84C] mb-4">
                  <i className="ri-check-line text-3xl"></i>
                </div>
                <h3 className="text-[#1a1208] text-xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  ¡Mensaje enviado!
                </h3>
                <p className="text-[#5a4a35] text-sm">Te responderé en menos de 24 horas.</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-[#C9A84C] text-sm underline cursor-pointer"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[#9a8a75] text-xs uppercase tracking-widest mb-2 block">Nombre *</label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      placeholder="Tu nombre"
                      className="w-full bg-[#faf7f2] border border-[#e8dcc8] rounded-lg px-4 py-3 text-[#3a2e1e] text-sm placeholder-[#b8a898] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                  <div>
                    <label className="text-[#9a8a75] text-xs uppercase tracking-widest mb-2 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="tu@email.com"
                      className="w-full bg-[#faf7f2] border border-[#e8dcc8] rounded-lg px-4 py-3 text-[#3a2e1e] text-sm placeholder-[#b8a898] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[#9a8a75] text-xs uppercase tracking-widest mb-2 block">Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    placeholder="+34 000 000 000"
                    className="w-full bg-[#faf7f2] border border-[#e8dcc8] rounded-lg px-4 py-3 text-[#3a2e1e] text-sm placeholder-[#b8a898] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="text-[#9a8a75] text-xs uppercase tracking-widest mb-2 block">Servicio de interés</label>
                  <select
                    name="servicio"
                    className="w-full bg-[#faf7f2] border border-[#e8dcc8] rounded-lg px-4 py-3 text-[#3a2e1e] text-sm focus:outline-none focus:border-[#C9A84C] transition-colors duration-200 cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Lectura General de Tarot">Lectura General de Tarot - 25€</option>
                    <option value="Lectura de Amor y Relaciones">Lectura de Amor y Relaciones - 20€</option>
                    <option value="Lectura Anual Completa">Lectura Anual Completa - 70€</option>
                  </select>
                </div>
                <div>
                  <label className="text-[#9a8a75] text-xs uppercase tracking-widest mb-2 block">Mensaje *</label>
                  <textarea
                    name="mensaje"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Cuéntame en qué puedo ayudarte..."
                    className="w-full bg-[#faf7f2] border border-[#e8dcc8] rounded-lg px-4 py-3 text-[#3a2e1e] text-sm placeholder-[#b8a898] focus:outline-none focus:border-[#C9A84C] transition-colors duration-200 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#C9A84C] text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-[#b8943e] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? "Enviando..." : "Enviar mensaje"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
